/**
 * CampaignLibrary - History of generated Campaign Packs
 * List view with thumbnails, dates, actions (open, copy, delete, re-publish)
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import {
  Library, Loader2, Trash2, ExternalLink, Copy, Send,
  Calendar, ImageIcon, Sparkles, ArrowRight, Facebook
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return isoStr.slice(0, 10); }
}

const CARD_TYPE_LABELS = {
  facebook_post: 'FB',
  instagram_caption: 'IG',
  cta: 'CTA',
  promo_idea: 'Promo',
};

export default function CampaignLibrary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchLibrary(); }, []);

  const fetchLibrary = async () => {
    try {
      const res = await axios.get(`${API}/booster/campaign-library`, { headers: authHeaders });
      setCampaigns(res.data.campaigns || []);
    } catch { toast.error('Nie udało się pobrać biblioteki'); }
    finally { setLoading(false); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Usunąć tę kampanię?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/booster/campaign-library/${id}`, { headers: authHeaders });
      setCampaigns(prev => prev.filter(c => c.id !== id));
      toast.success('Kampania usunięta');
    } catch { toast.error('Nie udało się usunąć'); }
    finally { setDeleting(null); }
  };

  const copyCampaignLink = (id) => {
    const url = `${window.location.origin}/kampania/${id}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link skopiowany!')).catch(() => toast.error('Nie udało się skopiować'));
  };

  if (loading) {
    return <PanelLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--apisai-primary)' }} /></div></PanelLayout>;
  }

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto" data-testid="campaign-library">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--apisai-primary)' }}>
                <Library className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--apisai-text)' }}>Biblioteka kampanii</h1>
                <p className="text-sm" style={{ color: 'var(--apisai-text-secondary)' }}>Historia wygenerowanych pakietów marketingowych</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/panel/booster')}
              className="rounded-xl h-9 px-4 text-xs font-medium text-white flex-shrink-0"
              style={{ background: 'var(--apisai-primary)' }}
              data-testid="new-campaign-btn"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Nowa kampania
            </Button>
          </div>
        </motion.div>

        {/* Empty state */}
        {campaigns.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border p-8 text-center" style={{ background: 'white', borderColor: 'var(--apisai-border)' }} data-testid="library-empty">
            <Library className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--apisai-text-muted)' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--apisai-text)' }}>Brak kampanii</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--apisai-text-muted)' }}>Wygeneruj swoją pierwszą kampanię marketingową.</p>
            <Button onClick={() => navigate('/panel/booster')} className="rounded-xl h-10 px-6 text-white font-medium" style={{ background: 'var(--apisai-primary)' }}>
              Stwórz kampanię <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* Campaign list */}
        <div className="space-y-3">
          {campaigns.map((campaign, i) => (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden group hover:shadow-md transition-shadow"
              style={{ background: 'white', borderColor: 'var(--apisai-border)' }}
              data-testid={`campaign-item-${campaign.id}`}
            >
              <div className="flex items-stretch">
                {/* Thumbnail */}
                <div className="w-32 h-28 flex-shrink-0 relative overflow-hidden" style={{ background: '#F3F4F6' }}>
                  {campaign.thumbnail ? (
                    <img src={campaign.thumbnail} alt="Kampania" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8" style={{ color: 'var(--apisai-text-muted)' }} />
                    </div>
                  )}
                  {i === 0 && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[9px] font-bold text-white" style={{ background: 'var(--apisai-primary)' }}>
                      NAJNOWSZA
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>
                        Kampania marketingowa
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: '#EBF5FF', color: '#1877F2' }}>
                        AI Marketing Pack
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--apisai-text-muted)' }}>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(campaign.created_at)}
                      </span>
                      <span>{campaign.cards_count} materiały</span>
                    </div>
                    {/* Card type badges */}
                    <div className="flex gap-1 mt-2">
                      {campaign.card_types.map(type => (
                        <span key={type} className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: '#F3F4F6', color: 'var(--apisai-text-secondary)' }}>
                          {CARD_TYPE_LABELS[type] || type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 px-4 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/panel/booster/kampania/${campaign.id}`)}
                    className="rounded-xl h-8 text-xs px-3 border"
                    style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }}
                    data-testid={`open-campaign-${campaign.id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1" /> Otwórz
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyCampaignLink(campaign.id)}
                    className="rounded-xl h-8 text-xs px-3 border"
                    style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }}
                    data-testid={`copy-link-${campaign.id}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteCampaign(campaign.id)}
                    disabled={deleting === campaign.id}
                    className="rounded-xl h-8 text-xs px-3 border hover:bg-red-50 hover:border-red-200 hover:text-red-500"
                    style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-muted)' }}
                    data-testid={`delete-campaign-${campaign.id}`}
                  >
                    {deleting === campaign.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </PanelLayout>
  );
}
